import { TabStyles } from '@tableau/widgets';
import { NestedCSSProperties } from 'typestyle/lib/types';

import { CommonStyles } from '../../../common/react/CommonStyles';
import { cssClass, Style } from '../../../style';
import { SpacesCommonStyles } from '../SpacesStyles';


const cssPrefix = 'SpaceHeaderStyles';

function buildCss(className: string, ...properties: Style[]): string {
  return cssClass(cssPrefix, className, ...properties);
}

const row: NestedCSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  $nest: {
    '&>*': {
      marginRight: TabStyles.Spacing.MediumSpace
    }
  }
};

const column: NestedCSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  $nest: {
    '&>*': {
      marginBottom: TabStyles.Spacing.DefaultSpace
    }
  }
};

const truncated: NestedCSSProperties = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  minWidth: 0
};

const center: NestedCSSProperties = {
  alignItems: 'center'
};

const MetadataStyles = {
  color: TabStyles.Typography.Color.Disabled,
  marginRight: TabStyles.Spacing.DefaultSpace
};

export namespace SpaceHeaderStyles {
  export const Row  = buildCss('row', row);
  export const RowAlignedCenter = buildCss('row_center', row, center, truncated);
  export const Column = buildCss('column', column, truncated);

  const MastheadIconWidthPx = '60px';
  export const IconDivWrapper = cssClass(cssPrefix, 'icon-div-wrapper', {
    minWidth: MastheadIconWidthPx,
    height: '60px',
    position: 'relative'
  });

  export const Icon = cssClass(cssPrefix, 'icon', {
    height: '60px',
    width: MastheadIconWidthPx,
    position: 'absolute',
    // This vertically aligns it with the owner text
    top: '-5px',
    left: 0,
  });

  export const SiteUserIcon = cssClass(cssPrefix, 'site-usericon', {
    height: '60px',
    width: MastheadIconWidthPx,
    position: 'absolute',
    // This vertically aligns it with the owner text
    top: '-5px',
    left: 0,
    borderRadius: '50%',
    objectFit: 'cover'
  });

  export const Title = buildCss('title', truncated, {
    fontWeight: 'normal',
    fontSize: SpacesCommonStyles.TitleFontSize,
    margin: '0',
    marginRight: TabStyles.Spacing.ExtraLargeSpace,
    height: SpacesCommonStyles.RowHeight,
    lineHeight: SpacesCommonStyles.RowHeight,
  });

  export const Metadata = cssClass(cssPrefix, 'metadata', {
    fontWeight: 'normal',
    marginTop: TabStyles.Spacing.SmallSpace,
    alignItems: 'center',
    display: 'inline-flex',
    verticalAlign: 'middle',
    justifyContent: 'center',
    flexDirection: 'row',
    fontSize: SpacesCommonStyles.SubtitleFontSize,
  });

  export const UserLabel = cssClass(cssPrefix, 'userlabel',
    {...MetadataStyles});

  export const ScheduleLabel = cssClass(cssPrefix, 'schedule-label',
    {...MetadataStyles});

  export const GroupLabel = cssClass(cssPrefix, 'group-label',
    {...MetadataStyles});

  export const headerLinkStyle: React.CSSProperties = {
    fontWeight: 'bold',
  };

  export const headerLink = cssClass(cssPrefix, 'headerLink', headerLinkStyle);

  export const userEmailHeaderLink = cssClass(cssPrefix, 'user-email-header-link', {
    ...headerLinkStyle,
    marginLeft: TabStyles.Spacing.ExtraLargeSpace
  });

  const encryptionStatusNest: any = {};
  encryptionStatusNest[`& .${headerLink}`] = {
    textDecoration: 'none'
  };
  encryptionStatusNest[`& .disabled`] = {
    fontWeight: 'normal',
    color: '#9B9B9B'
  };

  export const encryptionStatus = cssClass(cssPrefix, 'encryptionStatus', {
    display: 'inline-flex',
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
    $nest: encryptionStatusNest
  });

  export const ExtractStatus = cssClass(cssPrefix, 'extractStatus', {
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
  });

  export const ExtractStatusLabel = cssClass(cssPrefix, 'extractStatusLabel', {
    display: 'inline-block',
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
  });

  export const ExtractStatusSelection = cssClass(cssPrefix, 'extractStatusSelection', {
    display: 'inline-block',
    $nest: {
      '&>img': {
        position: 'fixed',
      }
    }
  });

  export const ExtractStatusSpinner = cssClass(cssPrefix, 'extractStatusSpinner', {
    display: 'inline-block',
    verticalAlign: 'sub',
    marginLeft: TabStyles.Spacing.DefaultSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
  });

  export const ExtractStatusText = cssClass(cssPrefix, 'extractStatusText', {
    display: 'inline-block',
    marginLeft: TabStyles.Spacing.DefaultSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
    $nest: encryptionStatusNest
  });

  export const DataLabel = cssClass(cssPrefix, 'datalabel',
    {...MetadataStyles,
      marginLeft: TabStyles.Spacing.ExtraLargeSpace,});

  export const DataStatusIcon = cssClass(cssPrefix, 'dataStatusIcon', {
        display: 'flex',
  });

  export const TextOnlyMetadataLabelWithLeftMargin = cssClass(cssPrefix, 'textOnlyLabelWithLeftMargin', {
    ...MetadataStyles,
    marginLeft: TabStyles.Spacing.ExtraLargeSpace
  });

  export const MetadataInfo = cssClass(cssPrefix, 'metadatainfo', {
    ...MetadataStyles,
    fontWeight: 'bold',
  });

  export const CertificationIcon = cssClass(cssPrefix, 'icon', {
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    height: '16px',
    width: '16px',
    position: 'relative',
    left: 0,
    top: '3px'
  });

  export const TextAndIconMetadataLabel = cssClass(cssPrefix, 'textAndIconMetadataLabel', {
    marginLeft: TabStyles.Spacing.SmallSpace,
    ...MetadataStyles,
  });

  export const ItemDescription = cssClass(cssPrefix, 'description', {
    fontWeight: 'normal',
    fontSize: TabStyles.Typography.Sizing.Default,
    color: TabStyles.Typography.Color.PrimaryLight,
    overflow: 'hidden',
    position: 'relative',
    maxHeight: 35,
    textOverflow: 'ellipsis',
    whiteSpace: 'normal'
  });

  export const FavoriteTooltip = cssClass(cssPrefix, 'favoriteTooltip', {
    fontWeight: 'bold',
    fontSize: SpacesCommonStyles.SubtitleFontSize,
    textAlign: 'center'
  });

  export const SpotlightActionButtonsRow = cssClass(cssPrefix, 'spotlightActionsRow', {
    marginTop: TabStyles.Spacing.MediumSpace
  });

  export const InfoDetailContent = cssClass(cssPrefix, 'InfoDetailContent', {
    backgroundColor: 'white',
    paddingRight: TabStyles.Spacing.SmallSpace,
    paddingLeft: TabStyles.Spacing.SmallSpace
  });

  export const InfoDetailImage = cssClass(cssPrefix, 'InfoDetailImage', {
    width: 18,
    height: 18,
    verticalAlign: 'middle',
    cursor: 'pointer'
  });
}
